/*
 * JSON-LD schema builders for Xtreme Care Services.
 * These structured-data objects are injected into <head> via the SEO component
 * so Google can render richer SERP snippets (LocalBusiness, FAQ, breadcrumbs)
 * and eligibility for Maps / Knowledge Panel results.
 */

const BASE = "https://xtremecare.ca";
const PHONE = "+1-807-355-8600";
const EMAIL = "admin@xtremecare.ca";
const LOGO = `${BASE}/logo.png`;
const SAME_AS = []; // add social profile URLs here when available

const ORG_BASE = {
  "@type": "Organization",
  name: "Xtreme Care Services",
  url: BASE,
  logo: LOGO,
  email: EMAIL,
  telephone: PHONE,
  sameAs: SAME_AS,
};

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    ...ORG_BASE,
    description:
      "Xtreme Care Services is a Canadian home care agency and healthcare staffing agency delivering personal support workers, RPNs and RNs to families and facilities in every province and territory.",
    areaServed: {
      "@type": "Country",
      name: "Canada",
    },
  };
}

/**
 * National LocalBusiness — used on the homepage & division pages.
 */
export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "@id": `${BASE}/#business`,
    name: "Xtreme Care Services",
    alternateName: "Xtreme Care Home Care & Healthcare Staffing",
    url: BASE,
    logo: LOGO,
    image: LOGO,
    telephone: PHONE,
    email: EMAIL,
    priceRange: "$$",
    description:
      "Nationwide home care agency and healthcare staffing agency serving seniors, families and healthcare facilities across Canada.",
    areaServed: {
      "@type": "Country",
      name: "Canada",
    },
    address: {
      "@type": "PostalAddress",
      addressCountry: "CA",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: PHONE,
        email: EMAIL,
        contactType: "customer service",
        areaServed: "CA",
        availableLanguage: ["English", "French"],
      },
    ],
    sameAs: SAME_AS,
  };
}

/**
 * City-scoped LocalBusiness — one per /locations/:province/:city page.
 * Uses a stable @id per city so Google can consolidate signals.
 */
export function cityLocalBusinessSchema({ city, provinceName, provinceCode, path }) {
  return {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "@id": `${BASE}${path}#business`,
    name: `Xtreme Care Services — ${city}, ${provinceCode}`,
    url: `${BASE}${path}`,
    logo: LOGO,
    image: LOGO,
    telephone: PHONE,
    email: EMAIL,
    priceRange: "$$",
    description: `Compassionate home care and PSW staffing in ${city}, ${provinceName}. Bonded, insured, background-checked caregivers from Xtreme Care Services.`,
    parentOrganization: {
      "@type": "Organization",
      name: "Xtreme Care Services",
      url: BASE,
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: city,
      addressRegion: provinceCode,
      addressCountry: "CA",
    },
    areaServed: [
      { "@type": "City", name: city, containedInPlace: { "@type": "AdministrativeArea", name: provinceName } },
    ],
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: PHONE,
        email: EMAIL,
        contactType: "customer service",
        areaServed: provinceCode,
        availableLanguage: ["English", "French"],
      },
    ],
  };
}

/**
 * Service schema for /services/:slug pages.
 */
export function serviceSchema({ service, path }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `${BASE}${path}#service`,
    name: service.title,
    description: service.meta_description,
    url: `${BASE}${path}`,
    provider: { "@type": "Organization", name: "Xtreme Care Services", url: BASE },
    areaServed: { "@type": "Country", name: "Canada" },
    serviceType: service.title,
  };
}

/**
 * FAQPage schema — pass in an array of { q, a } objects.
 */
export function faqSchema(faqs) {
  if (!faqs || !faqs.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

/**
 * BreadcrumbList schema — pass ordered [{ name, path }] items.
 */
export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: `${BASE}${it.path}`,
    })),
  };
}
